import '../styles/GlobalDesign.css';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.NODE_ENV === 'production'
  ? '/api'
  : process.env.REACT_APP_API_URL || '/api';

const AGGREGATORS = [
  { key: 'moneyfusion', label: 'MoneyFusion', icon: '🟢', accent: '#22C55E' },
  { key: 'leekpay',     label: 'LeekPay',     icon: '🔵', accent: '#3B82F6' },
];

const BTN = (bg, color = '#fff') => ({
  padding: '8px 16px', background: bg, border: 'none', borderRadius: '8px',
  color, fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap',
});

const AdminPaymentAggregators = () => {
  const [config, setConfig] = useState(null);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/admin/payment-aggregators`, authHeaders());
      setConfig(data.config);
      setCountries(data.countries || []);
    } catch (e) {
      setMessage({ type: 'error', text: "Impossible de charger la configuration" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleEnabled = (key) => {
    setConfig(c => ({ ...c, [key]: { ...c[key], enabled: !c[key].enabled } }));
  };

  const toggleCountry = (key, code) => {
    setConfig(c => {
      const has = c[key].countries.includes(code);
      const next = has ? c[key].countries.filter(x => x !== code) : [...c[key].countries, code];
      return { ...c, [key]: { ...c[key], countries: next } };
    });
  };

  const setAllCountries = (key, all) => {
    setConfig(c => ({ ...c, [key]: { ...c[key], countries: all ? countries.map(x => x.code) : [] } }));
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const { data } = await axios.put(`${API_URL}/admin/payment-aggregators`, {
        moneyfusion: config.moneyfusion,
        leekpay: config.leekpay,
      }, authHeaders());
      setConfig(data.config);
      setMessage({ type: 'success', text: 'Configuration enregistrée' });
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || "Échec de l'enregistrement" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="admin-section">
        <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 className="section-title">💳 Agrégateurs de paiement</h1>
            <p className="section-subtitle">Activez/désactivez MoneyFusion et LeekPay, et choisissez les pays couverts par chacun</p>
          </div>
          <button onClick={load} style={BTN('rgba(255,255,255,0.07)', '#94A3B8')}>🔄 Actualiser</button>
        </div>

        {message && (
          <div style={{
            margin: '12px 0', padding: '10px 14px', borderRadius: 8,
            background: message.type === 'success' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
            border: `1px solid ${message.type === 'success' ? '#22C55E' : '#EF4444'}`,
            color: message.type === 'success' ? '#4ADE80' : '#F87171', fontSize: '0.88rem',
          }}>{message.text}</div>
        )}

        {loading || !config ? (
          <div style={{ color: '#94A3B8', padding: 20 }}>Chargement…</div>
        ) : (
          <>
            {AGGREGATORS.map(({ key, label, icon, accent }) => {
              const agg = config[key];
              const selectedCount = agg.countries.length;
              return (
                <div key={key} className="admin-card" style={{ padding: 18, marginBottom: 18 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: '1.3rem' }}>{icon}</span>
                      <h2 style={{ margin: 0, color: '#F1F5F9', fontSize: '1.1rem' }}>{label}</h2>
                      <span style={{
                        fontSize: '0.72rem', padding: '3px 10px', borderRadius: 999,
                        background: agg.enabled ? 'rgba(34,197,94,0.18)' : 'rgba(148,163,184,0.18)',
                        color: agg.enabled ? '#4ADE80' : '#94A3B8', fontWeight: 700,
                      }}>{agg.enabled ? 'ACTIVÉ' : 'DÉSACTIVÉ'}</span>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                      <span style={{ color: '#94A3B8', fontSize: '0.85rem' }}>Activer</span>
                      <input
                        type="checkbox"
                        checked={agg.enabled}
                        onChange={() => toggleEnabled(key)}
                        style={{ width: 20, height: 20, accentColor: accent, cursor: 'pointer' }}
                      />
                    </label>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
                    <span style={{ color: '#CBD5E1', fontSize: '0.85rem' }}>
                      Pays couverts <strong style={{ color: accent }}>({selectedCount}/{countries.length})</strong>
                    </span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => setAllCountries(key, true)} style={BTN('rgba(255,255,255,0.07)', '#94A3B8')}>Tout cocher</button>
                      <button onClick={() => setAllCountries(key, false)} style={BTN('rgba(255,255,255,0.07)', '#94A3B8')}>Tout décocher</button>
                    </div>
                  </div>

                  <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(165px, 1fr))',
                    gap: 8, opacity: agg.enabled ? 1 : 0.5,
                  }}>
                    {countries.map(country => {
                      const checked = agg.countries.includes(country.code);
                      return (
                        <label key={country.code} style={{
                          display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                          background: checked ? `${accent}22` : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${checked ? accent : 'rgba(255,255,255,0.1)'}`,
                          borderRadius: 8, cursor: 'pointer', fontSize: '0.84rem', color: '#E2E8F0',
                        }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleCountry(key, country.code)}
                            style={{ accentColor: accent, cursor: 'pointer' }}
                          />
                          <span>{country.flag} {country.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={save} disabled={saving} style={BTN('#6366F1')}>
                {saving ? 'Enregistrement…' : '💾 Enregistrer'}
              </button>
            </div>

            <p style={{ color: '#64748B', fontSize: '0.78rem', marginTop: 14, lineHeight: 1.5 }}>
              Note : LeekPay n'est réellement proposé aux clients que si ses clés API sont configurées côté serveur.
              Désactivez un agrégateur pour basculer tout le trafic Mobile Money vers l'autre.
            </p>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPaymentAggregators;
